import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider, message } from 'antd';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchAdminMerchantEarnings,
  fetchMerchantEarningsDetails,
  exportAdminMerchantEarningsCSV,
  setDateRange,
  setPage,
  setLimit,
  setSort,
  setSearchTerm,
  setSelectedMerchant,
  clearError,
} from '../../../redux/merchantEarningsSlice';
import { PageWrapper } from './Styles';
import HeaderSection from './HeaderSection';
import FiltersSection from './FiltersSection';
import SummaryStats from './SummaryStats';
import MerchantsTable from './MerchantsTable';
import MerchantDetailsModal from './MerchantDetailsModal';
import ErrorSection from './ErrorSection';
import type { MerchantEarningsRow } from './types';

const EXPORT_SUCCESS = 'Merchants earnings data exported to CSV';
const EXPORT_FAILED = 'Failed to export earnings data';
const DETAILS_FAILED = 'Failed to load merchant details';

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
const formatPercentage = (value: number): string => `${(value || 0).toFixed(1)}%`;

const MerchantEarningsOverview: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const {
    rows,
    pagination,
    summaryStats,
    dateRange,
    page,
    limit,
    sortBy,
    sortOrder,
    searchTerm,
    loading,
    error,
    selectedMerchant,
    merchantDetails,
    detailsLoading,
  } = useSelector((state: RootState) => state.merchantEarningsOverview);

  const fetchList = useCallback(() => {
    dispatch(fetchAdminMerchantEarnings());
  }, [dispatch]);

  useEffect(() => {
    fetchList();
  }, [fetchList, dateRange.startDate, dateRange.endDate, page, limit, sortBy, sortOrder]);

  const themeConfig = React.useMemo(
    () => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }),
    [isDarkMode]
  );
  const antdConfig = React.useMemo(() => getAntdThemeConfig(isDarkMode), [isDarkMode]);

  const handleBack = useCallback(() => navigate(-1), [navigate]);
  const handleDateRangeChange = useCallback(
    (range: { startDate: string; endDate: string }) => dispatch(setDateRange(range)),
    [dispatch]
  );
  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: 'asc' | 'desc') =>
      dispatch(setSort({ sortBy: newSortBy, sortOrder: newSortOrder })),
    [dispatch]
  );
  const handlePaginationChange = useCallback(
    (p: number, pageSize: number) => {
      dispatch(setPage(p));
      dispatch(setLimit(pageSize));
    },
    [dispatch]
  );
  const handleViewDetails = useCallback(
    (row: MerchantEarningsRow) => {
      dispatch(setSelectedMerchant(row));
      dispatch(fetchMerchantEarningsDetails({ merchantId: row.merchantId }))
        .unwrap()
        .catch(() => message.error(DETAILS_FAILED));
    },
    [dispatch]
  );
  const handleCloseModal = useCallback(
    () => dispatch(setSelectedMerchant(null)),
    [dispatch]
  );

  const handleExport = useCallback(async () => {
    try {
      const result = await dispatch(exportAdminMerchantEarningsCSV()).unwrap();
      const blob = new Blob([result], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `merchants-earnings-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success(EXPORT_SUCCESS);
    } catch {
      message.error(EXPORT_FAILED);
    }
  }, [dispatch]);

  const filteredRows = rows.filter(
    (r) =>
      (r.merchantName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (error && rows.length === 0) {
    return (
      <ThemeProvider theme={themeConfig}>
        <ConfigProvider theme={antdConfig}>
          <PageWrapper>
            <ErrorSection
              error={error}
              onRetry={() => {
                dispatch(clearError());
                fetchList();
              }}
              onBack={handleBack}
              loading={loading}
            />
          </PageWrapper>
        </ConfigProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={themeConfig}>
      <ConfigProvider theme={antdConfig}>
        <PageWrapper>
          <HeaderSection
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onBack={handleBack}
            onExport={handleExport}
            onRefresh={fetchList}
            refreshLoading={loading}
          />
          <FiltersSection
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            searchTerm={searchTerm}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onDateRangeChange={handleDateRangeChange}
            onSearchChange={(v) => dispatch(setSearchTerm(v))}
            onSortChange={handleSortChange}
          />
          {summaryStats && (
            <SummaryStats
              stats={summaryStats}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
            />
          )}
          <MerchantsTable
            rows={rows}
            filteredRows={filteredRows}
            pagination={pagination}
            loading={loading}
            formatCurrency={formatCurrency}
            onViewDetails={handleViewDetails}
            onPaginationChange={handlePaginationChange}
          />
          <MerchantDetailsModal
            open={!!selectedMerchant}
            merchant={selectedMerchant}
            details={merchantDetails}
            detailsLoading={detailsLoading}
            formatCurrency={formatCurrency}
            onClose={handleCloseModal}
          />
        </PageWrapper>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default MerchantEarningsOverview;
